import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import * as notificationsAPI from '@/api/modules/notifications'

/**
 * Async thunk: Fetch unread notifications
 */
export const fetchUnreadNotifications = createAsyncThunk(
  'notification/fetchUnread',
  async (_, { rejectWithValue }) => {
    try {
      const notifications = await notificationsAPI.fetchUnreadNotifications()
      return notifications
    } catch (error) {
      return rejectWithValue(
        error.apiError?.message || error.message || 'Failed to fetch notifications'
      )
    }
  }
)

/**
 * Async thunk: Fetch all notifications
 */
export const fetchAllNotifications = createAsyncThunk(
  'notification/fetchAll',
  async (params = {}, { rejectWithValue }) => {
    try {
      const notifications = await notificationsAPI.fetchAllNotifications(params)
      return notifications
    } catch (error) {
      return rejectWithValue(
        error.apiError?.message || error.message || 'Failed to fetch notifications'
      )
    }
  }
)

/**
 * Async thunk: Mark notification as read
 */
export const markNotificationAsReadAsync = createAsyncThunk(
  'notification/markAsRead',
  async (notificationId, { rejectWithValue }) => {
    try {
      await notificationsAPI.markNotificationsAsRead([notificationId])
      return notificationId
    } catch (error) {
      return rejectWithValue(
        error.apiError?.message || error.message || 'Failed to mark as read'
      )
    }
  }
)

/**
 * Async thunk: Mark all as read
 */
export const markAllAsReadAsync = createAsyncThunk(
  'notification/markAllAsRead',
  async (_, { rejectWithValue }) => {
    try {
      await notificationsAPI.markAllNotificationsAsRead()
      return true
    } catch (error) {
      return rejectWithValue(
        error.apiError?.message || error.message || 'Failed to mark all as read'
      )
    }
  }
)

/**
 * Async thunk: Fetch unread count
 */
export const fetchUnreadCount = createAsyncThunk(
  'notification/fetchUnreadCount',
  async (_, { rejectWithValue }) => {
    try {
      const count = await notificationsAPI.getUnreadCount()
      return count
    } catch (error) {
      return rejectWithValue(
        error.apiError?.message || error.message || 'Failed to fetch unread count'
      )
    }
  }
)

/**
 * Reduce a fetched notification list into state.
 * Guards against a non-array payload so a reducer throw can never leave the
 * notification panel wedged on its loading state.
 *
 * @param {Object} state - Slice draft state
 * @param {Object} action - Fulfilled action with the notification list payload
 */
function applyFetchedNotifications(state, action) {
  const notifications = Array.isArray(action.payload) ? action.payload : []
  state.loading = false
  state.notifications = notifications
  state.unreadCount = notifications.filter((n) => !n.is_read).length
}

/**
 * Notification Slice
 */
const notificationSlice = createSlice({
  name: 'notification',
  initialState: {
    notifications: [],
    unreadCount: 0,
    loading: false,
    error: null,
  },
  reducers: {
    addNotification: (state, action) => {
      // Add new notification to top of list
      state.notifications.unshift(action.payload)
      // Increment unread count if not already read
      if (!action.payload.is_read) {
        state.unreadCount += 1
      }
    },
    setUnreadCount: (state, action) => {
      state.unreadCount = action.payload
    },
    markNotificationAsRead: (state, action) => {
      const notification = state.notifications.find(
        (n) => n.id === action.payload
      )
      if (notification && !notification.is_read) {
        notification.is_read = true
        state.unreadCount = Math.max(0, state.unreadCount - 1)
      }
    },
    markAllAsRead: (state) => {
      state.notifications.forEach((n) => {
        n.is_read = true
      })
      state.unreadCount = 0
    },
    clearNotifications: (state) => {
      state.notifications = []
      state.unreadCount = 0
    },
  },
  extraReducers: (builder) => {
    // Fetch unread notifications
    builder
      .addCase(fetchUnreadNotifications.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchUnreadNotifications.fulfilled, applyFetchedNotifications)
      .addCase(fetchUnreadNotifications.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    // Fetch all notifications
    builder
      .addCase(fetchAllNotifications.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAllNotifications.fulfilled, applyFetchedNotifications)
      .addCase(fetchAllNotifications.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    // Mark as read
    builder.addCase(markNotificationAsReadAsync.fulfilled, (state, action) => {
      const notification = state.notifications.find(
        (n) => n.id === action.payload
      )
      if (notification && !notification.is_read) {
        notification.is_read = true
        state.unreadCount = Math.max(0, state.unreadCount - 1)
      }
    })

    // Mark all as read
    builder.addCase(markAllAsReadAsync.fulfilled, (state) => {
      state.notifications.forEach((n) => {
        n.is_read = true
      })
      state.unreadCount = 0
    })

    // Fetch unread count
    builder
      .addCase(fetchUnreadCount.pending, (state) => {
        state.error = null
      })
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload
      })
      .addCase(fetchUnreadCount.rejected, (state, action) => {
        state.error = action.payload
      })
  },
})

export const {
  addNotification,
  setUnreadCount,
  markNotificationAsRead,
  markAllAsRead,
  clearNotifications,
} = notificationSlice.actions

export default notificationSlice.reducer
